import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getFaqs } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { HelpCircle, Info, Shield } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { createFaqSeo } from '@/lib/seoConfig';

// Pricing explanation items
const PRICING_EXPLAINED = [
  { term: 'Supplier Cost', desc: 'Either a fixed final supplier quote or live fine-metal value plus that product’s supplier-specific wastage, making and other charges' },
  { term: 'SŌHM Margin', desc: 'Defaults to 13% of supplier cost and may be reduced by admin for a product discount' },
  { term: 'GST', desc: 'Applied after supplier cost and the SŌHM margin using the configured GST rate' },
  { term: 'Displayed Price', desc: '(Supplier cost + selected SŌHM margin) + GST, with no global wastage percentage' },
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getFaqs();
        setFaqs(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const categories = ['all', ...new Set(faqs.map(f => f.category))];
  const filtered = filter === 'all' ? faqs : faqs.filter(f => f.category === filter);
  const faqSeo = createFaqSeo(faqs);

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="faq-page">
      <Seo {...faqSeo} />
      <Navbar />
      <div className="grain-overlay" />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">FAQ</h1>
          </div>
          <p className="text-muted-foreground mb-8">Frequently asked questions about SŌHM, purity, and collectibles</p>
        </motion.div>

        {/* How Pricing Works Section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }}
          className="bg-primary/5 border border-primary/20 rounded-sm p-6 mb-8"
          data-testid="pricing-explained"
        >
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-primary" />
            <h2 className="font-display text-lg font-bold uppercase">How Pricing Works</h2>
          </div>
          
          <div className="grid gap-3 mb-4">
            {PRICING_EXPLAINED.map((item, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="font-medium text-primary min-w-[140px]">{item.term}</span>
                <span className="text-muted-foreground">{item.desc}</span>
              </div>
            ))}
          </div>
          
          <Separator className="bg-primary/20 my-4" />
          
          <div className="flex items-start gap-2 bg-black/20 rounded-sm p-3">
            <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Full Transparency:</strong> SŌHM shows supplier cost, the selected SŌHM margin and GST. Any wastage is supplier-specific and configured per product.
            </p>
          </div>
        </motion.div>

        <Separator className="bg-white/10 mb-8" />

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="faq-filters">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors flex-shrink-0 ${
                filter === cat ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:text-foreground border border-border/40'
              }`}
              data-testid={`faq-filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <Accordion type="single" collapsible className="space-y-2">
            {filtered.map((faq, i) => (
              <motion.div key={faq.faq_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <AccordionItem value={faq.faq_id} className="bg-card border border-border/40 rounded-sm px-4 data-[state=open]:border-primary/30" data-testid={`faq-item-${faq.faq_id}`}>
                  <AccordionTrigger className="font-display font-bold text-left hover:text-primary transition-colors py-4 text-sm">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
}
